export interface SingleFlight<T> {
	(): Promise<T>;
	/**
	 * Resolves once any in-flight run has settled, without starting one. Rejections
	 * are absorbed: callers use this to wait out a run, not to observe its result.
	 */
	settled: () => Promise<void>;
}

/**
 * Wraps an async function so concurrent callers share one run instead of starting
 * their own. There is no memoisation: once a run settles, the next call starts a
 * fresh one.
 */
export function singleFlight<T>(run: () => Promise<T>): SingleFlight<T> {
	let inFlight: Promise<T> | null = null;

	function guarded() {
		// Assigned before the first await, so a second caller can never observe `null`
		// while the first run is still starting.
		inFlight ??= run().finally(() => {
			inFlight = null;
		});

		return inFlight;
	}

	guarded.settled = async () => {
		await inFlight?.catch(() => {});
	};

	return guarded;
}
